import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  Button,
  ConsentOrdersPathName,
  FormError,
  FurnitureAndPersonalEffectsBaseProps,
  FurnitureAndPersonalEffectsBaseSchema,
  FurnitureProps_1,
  Furniture_InitialState,
  RadioSelect,
  useFurniture,
  useNames
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { selectAsset, selectBaseAssetsDebts, selectConsentOrdersBase } from "pages/ConsentOrders";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_furniture_or_personal_effects: "no"
};

interface IProps {
  formId: string;
  store: FurnitureProps_1;
  onSubmit: (values: FurnitureProps_1) => void;
}

const Step01: FC<IProps> = ({ formId, store, onSubmit }) => {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const assetsDebts = useSelector(selectBaseAssetsDebts);
  const usenames = useNames({ initialDetails });
  const { spouseFirstName } = usenames;
  const furnitureAndPersonalData = useSelector(
    selectAsset(AssetType.FURNITURE_AND_PERSONAL)
  ) as Furniture_InitialState[];

  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { handleSubmit, watch, control, reset, getValues } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(FurnitureAndPersonalEffectsBaseSchema)
  });

  const editData = (values?: FurnitureAndPersonalEffectsBaseProps) => {
    navigate(`${ConsentOrdersPathName.Furniture}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const _onSubmit = async (values: FurnitureProps_1) => {
    if (values.if_furniture_or_personal_effects === "no" && furnitureAndPersonalData.length) {
      setError(
        "If you don't have any items of furniture or personal effects in Australia, then please remove the added details."
      );
    } else if (values.if_furniture_or_personal_effects === "yes" && !furnitureAndPersonalData.length) {
      setError("please add items of furniture or personal effects.");
    } else {
      setError("");
      onSubmit(values);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(defaultValues);
    }
  }, [location]);

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useFurniture({
    furnitureAndPersonalData,
    assetsDebts,
    ...usenames
  });

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(_onSubmit)}>
      <RadioSelect
        title="Do you wish to mention in your consent orders any specific items of furniture or personal effects?"
        subTitle="Eg if you wish to include valuable of sentimental items like artwork, antiques, family heirloom, expensive jewellery."
        name="if_furniture_or_personal_effects"
        id="if_furniture_or_personal_effects"
        control={control}
        options={[
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" }
        ]}
        type="card"
        onChange={() => setError("")}
        theme="dark"
      />

      <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
        <p className="px-16 text-center text-base font-bold">
          Please add all the items of furniture or personal effects you wish to include in the consent orders
        </p>
        <Button
          className="px-10 py-2 uppercase"
          fontSize="sm"
          onClick={() => editData(undefined)}
          disabled={watch("if_furniture_or_personal_effects") !== "yes"}
        >
          + Add Item
        </Button>
      </div>

      {error ? (
        <div className="max-w-sm mx-auto text-center">
          <FormError error={error} />
        </div>
      ) : null}

      {Array.isArray(furnitureAndPersonalData) && furnitureAndPersonalData.length ? (
        <>
          <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

          {ownCard("user", editData)}
          {splitCard("user", editData)}
          <section className="flex justify-end items-center mb-12">
            <p className="text-gray-600">
              Total:
              <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
            </p>
          </section>

          <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

          {ownCard("spouse", editData)}
          {splitCard("spouse", editData)}
          <section className="flex justify-end items-center mb-12">
            <p className="text-gray-600">
              Total:
              <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
            </p>
          </section>
        </>
      ) : null}
    </form>
  );
};
export default Step01;
